import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, ChevronRight, Code2, Loader2, Send } from "lucide-react";
import { slotIdsForKind } from "@spectree/shared";
import type { FogbugzFormulatorCoverage, FogbugzFormulatorKind } from "@ttc-spectree/toolbox-contract";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import type { ToolboxInterviewSession, ToolboxTranscriptMessage } from "@/hooks/queries/use-fogbugz-toolbox-interview";
import { slotLabel } from "@/lib/intent-labels";
import { PROGRESS_PHRASE_MS, progressPhrase, type FogbugzFormulatorMessage } from "@/lib/toolbox/fogbugz-formulator";
import { cn } from "@/lib/utils";

/** How many of the kind's slots are answered, from SpecTree's coverage. Pure; exported for tests. */
export function coverageCounts(
  kind: FogbugzFormulatorKind,
  coverage: FogbugzFormulatorCoverage,
): { answered: number; total: number; remaining: string[] } {
  const total = slotIdsForKind(kind).length;
  const remaining = [...coverage.requiredRemaining, ...coverage.optionalRemaining];
  return { answered: Math.max(total - remaining.length, 0), total, remaining };
}

/** Report progress, from SpecTree's coverage: never Claude's own claim that it is done. */
function CoverageProgress({ kind, coverage }: { kind: FogbugzFormulatorKind; coverage: FogbugzFormulatorCoverage }) {
  const { answered, total, remaining } = coverageCounts(kind, coverage);
  const percent = total > 0 ? Math.round((answered / total) * 100) : 0;
  return (
    <div className="space-y-2 rounded-lg border border-white/[0.08] p-3" data-testid="fogbugz-formulator-coverage">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium">Report progress</span>
        <span className="text-muted-foreground" data-testid="fogbugz-formulator-coverage-count">
          {answered} of {total} answered
        </span>
      </div>
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-white/[0.08]"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={answered}
        aria-label="Report progress"
      >
        <div
          className={cn("h-full rounded-full transition-all", coverage.isComplete ? "bg-green-500" : "bg-primary")}
          style={{ width: `${String(percent)}%` }}
        />
      </div>
      {remaining.length > 0 && (
        <p className="text-xs text-muted-foreground">Still to cover: {remaining.map((id) => slotLabel(id)).join(", ")}</p>
      )}
    </div>
  );
}

/** The cycling "what Claude is doing" line while a turn runs. */
function ClaudeWorking({ mode, startedAt }: { mode: "opening" | "reply"; startedAt: number }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, PROGRESS_PHRASE_MS / 2);
    return () => {
      window.clearInterval(timer);
    };
  }, []);
  return (
    <div className="flex items-center gap-2.5 text-[13px] text-muted-foreground" role="status" data-testid="fogbugz-formulator-working">
      <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
      {progressPhrase(mode, now - startedAt)}…
    </div>
  );
}

function MessageRow({ message }: { message: ToolboxTranscriptMessage }) {
  return (
    <div className={cn("flex", message.role === "user" ? "justify-end" : "justify-start")} data-message-row>
      <div
        className={cn(
          "max-w-[80%] rounded-lg p-3",
          message.role === "user" ? "bg-primary text-primary-foreground" : "border border-white/[0.08] bg-white/[0.04]",
        )}
      >
        <p className="whitespace-pre-wrap text-sm">{message.content}</p>
      </div>
    </div>
  );
}

export interface InterviewPanelProps {
  session: ToolboxInterviewSession;
  /** The running turn, or null. */
  running: { mode: "opening" | "reply"; startedAt: number } | null;
  /** The answer sent for the running turn, shown until SpecTree has it. */
  pendingAnswer: string | null;
  /** The answer of a failed turn: put back into the answer box so it is never lost. */
  keptAnswer: string | null;
  /** A failed turn's plain message; Retry sends the same turn again. */
  failure: FogbugzFormulatorMessage | null;
  onRetry: () => void;
  /** Send the person's answer (the bridge runs the next turn). */
  onAnswer: (answer: string) => void;
  /** Coverage is complete: go to the review step. */
  onReview: () => void;
  /** Screenshots and event logs. */
  attachments?: ReactNode;
}

/**
 * The question/answer panel of the Toolbox FogBugz Formulator (epic
 * PI-SPEC-SW-E309, feature PI-SPEC-SW-1620): the transcript SpecTree keeps,
 * the pending question, coverage progress, what Claude found in the code and,
 * while a turn runs on the person's Claude, plain progress phrases. A failed
 * turn shows its plain message with Retry; the typed answer is kept.
 */
export function InterviewPanel({
  session,
  running,
  pendingAnswer,
  keptAnswer,
  failure,
  onRetry,
  onAnswer,
  onReview,
  attachments,
}: InterviewPanelProps) {
  const [draft, setDraft] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  const awaitingPerson = session.awaiting === "person";
  const awaitingReview = session.awaiting === "review";
  const busy = running !== null;
  const canSend = awaitingPerson && !busy && draft.trim().length > 0 && pendingAnswer === null;

  // A failed answer comes back into the box, so the person can send it again or change it.
  useEffect(() => {
    if (keptAnswer !== null) setDraft((current) => (current === "" ? keptAnswer : current));
  }, [keptAnswer]);

  const transcript = session.transcript;
  const lastIsQuestion = transcript.at(-1)?.role === "assistant" && transcript.at(-1)?.content === session.question;
  const messages: ToolboxTranscriptMessage[] = [
    ...transcript,
    ...(session.question && !lastIsQuestion ? [{ role: "assistant" as const, content: session.question }] : []),
    ...(pendingAnswer !== null ? [{ role: "user" as const, content: pendingAnswer }] : []),
  ];

  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messages.length]);

  const send = () => {
    const answer = draft.trim();
    if (!canSend || answer === "") return;
    setDraft("");
    onAnswer(answer);
  };

  // Retry a failed reply with what is in the box now (the kept answer, maybe edited); else the page retries the owed turn.
  const retry = () => {
    if (keptAnswer !== null && draft.trim() !== "") send();
    else onRetry();
  };

  return (
    <div className="flex flex-col gap-4" data-testid="fogbugz-formulator-interview">
      <CoverageProgress kind={session.kind} coverage={session.coverage} />

      <div
        ref={listRef}
        className="flex max-h-[50vh] min-h-[200px] flex-col gap-4 overflow-y-auto rounded-lg border border-white/[0.08] p-4"
        data-testid="fogbugz-formulator-messages"
      >
        {messages.length === 0 && !busy && (
          <p className="text-sm text-muted-foreground">Claude has not asked anything yet.</p>
        )}
        {messages.map((message, index) => (
          <MessageRow key={`${message.role}-${String(index)}`} message={message} />
        ))}
        {running && <ClaudeWorking mode={running.mode} startedAt={running.startedAt} />}
      </div>

      {session.codeFindings.length > 0 && (
        <details className="rounded-lg border border-white/[0.08] p-3 text-sm" data-testid="fogbugz-formulator-findings">
          <summary className="flex cursor-pointer items-center gap-2 font-medium">
            <Code2 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            What Claude found in the code ({session.codeFindings.length})
          </summary>
          <ul className="mt-2 space-y-1.5">
            {session.codeFindings.map((finding) => (
              <li key={`${finding.path}-${finding.note}`} className="text-xs">
                <code className="text-muted-foreground">{finding.path}</code>
                <span className="block">{finding.note}</span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {attachments}

      {failure && (
        <div
          className="flex items-start justify-between gap-3 rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-400"
          role="alert"
          data-testid="fogbugz-formulator-turn-failed"
        >
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <div className="flex flex-col gap-1">
              <span>{failure.text}</span>
              {failure.setup && (
                <Link to="/toolbox/setup" className="font-medium text-link hover:underline">
                  Open Setup checks
                </Link>
              )}
            </div>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={retry} disabled={busy}>
            Retry
          </Button>
        </div>
      )}

      {awaitingReview ? (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-green-500/20 bg-green-500/10 p-3 text-sm">
          <span>Claude has everything it needs. Review your report before it is filed.</span>
          <Button type="button" onClick={onReview} data-testid="fogbugz-formulator-to-review">
            Review the report
            <ChevronRight className="ml-1 h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      ) : (
        <div className="flex items-end gap-2">
          <Textarea
            aria-label="Your answer"
            value={draft}
            placeholder={awaitingPerson ? "Type your answer…" : "Wait for Claude's question"}
            disabled={!awaitingPerson || busy || pendingAnswer !== null}
            onChange={(event) => {
              setDraft(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                send();
              }
            }}
            className="min-h-[44px] flex-1"
          />
          <Button type="button" onClick={send} disabled={!canSend} aria-label="Send answer">
            <Send className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      )}
    </div>
  );
}
